import {
  Component,
  ChangeDetectionStrategy,
  ElementRef,
  inject,
  signal,
  computed,
  forwardRef,
  viewChild,
  effect
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl, ReactiveFormsModule } from '@angular/forms';
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { Recipient } from '../../models/message.model';
import { RecipientSearchService } from '../../services/recipient-search.service';
import { BadgeComponent, BadgeGroupComponent, IconComponent } from '@pansa/ui';
import { GroupDTO } from '@app/features/groups/models/group.model';
import { GroupColorPipe } from '@app/features/groups/pipes/group-color.pipe';


@Component({
  selector: 'rqa-recipient-select',
  imports: [CommonModule, ReactiveFormsModule, OverlayModule, IconComponent,BadgeGroupComponent, BadgeComponent, GroupColorPipe],
  templateUrl: './recipient-select.component.html',
  styleUrls: ['./recipient-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(
        () => RecipientSelectComponent
      )
    }
  ]
})
export class RecipientSelectComponent implements ControlValueAccessor {
  readonly recipientService = inject(RecipientSearchService);

  readonly inputRef = viewChild<ElementRef<HTMLInputElement>>('input');
  readonly searchControl = new FormControl('', { nonNullable: true });
  readonly selectedRecipients = signal<Recipient[]>([]);
  readonly searchQuery = signal('');
  readonly isOverlayOpen = computed(() => this.searchQuery().trim().length >= 2);


  readonly suggestions = computed(() => this.recipientService.recipientsResource.value());

  constructor() {
    this.searchControl.valueChanges.subscribe(value => {
      this.searchQuery.set(value);

      this.recipientService.query.set(value);
    });
  }

  selectRecipient(recipient: Recipient): void {
    console.log(recipient);
    const exists = this.selectedRecipients().some(
      item => this.getRecipientKey(item) === this.getRecipientKey(recipient)
    );

    if (exists) {
      return;
    }

    this.selectedRecipients.update(items => [
      ...items,
      recipient
    ]);

    this.searchControl.setValue('', {
      emitEvent: false
    });

    this.searchQuery.set('');

    this.onChange(this.selectedRecipients());
    this.onTouched();

    this.inputRef()?.nativeElement.focus();
  }

  removeRecipient(recipient: Recipient): void {
    this.selectedRecipients.update(items =>
      items.filter(
        item =>
          this.getRecipientKey(item) !==
          this.getRecipientKey(recipient)
      )
    );

    this.onChange(this.selectedRecipients());
    this.onTouched();
  }

  onClear(): void {
    this.selectedRecipients.set([]);

    this.searchControl.setValue('', {
      emitEvent: false
    });

    this.searchQuery.set('');

    this.onChange([]);

    this.inputRef()?.nativeElement.focus();
  }

  isGroup(recipient: Recipient): recipient is GroupDTO {
    return 'webRqaUsers' in recipient;
  }

  displayName(recipient: Recipient): string {
    if (this.isGroup(recipient)) {
      return recipient.name;
    }

    return `${recipient.firstName} ${recipient.lastName}`;
  }

  private getRecipientKey(recipient: Recipient): string {
    if (this.isGroup(recipient)) {
      return `group-${recipient.id}`;
    }

    return `user-${recipient.username}`;
  }


  // ------------------
  // CVA
  // ------------------

  private onChange = (_: Recipient[]) => { };

  private onTouched = () => { };

writeValue(value: Recipient[] | null): void {
  console.log('writeValue', value);
  console.log('isArray', Array.isArray(value));

  this.selectedRecipients.set(value ?? []);
}

  registerOnChange(fn: (value: Recipient[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    disabled
      ? this.searchControl.disable()
      : this.searchControl.enable();
  }

}